import {
    CommonModule,
    CurrencyPipe,
    DatePipe,
    Location,
    LocationStrategy,
    PathLocationStrategy
} from '@angular/common';
import { Injector, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxPaginationModule } from 'ngx-pagination';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { DropzoneConfigInterface, DropzoneModule, DROPZONE_CONFIG } from 'ngx-dropzone-wrapper';
import { AppRoutingModule } from './app.routes';
import { UcrTextModule } from './db-text/ucr-text.module';
import { AppComponent } from './app.component';
import { rootRouterConfig } from './app.routes';
import { UcrDialogModule } from './dialog/ucr-dialog.module';
import { EmailModule } from './email/email.module';
import { UcrFormsModule } from './forms/ucrforms.module';
import { GradeSubmitStatusComponent } from './roster/grade-submit-status/grade-submit-status-component';
import { RosterAddCommentComponent } from './roster/roster-add-comment/roster-add-comment.component';
import { EnrollmentSourceService } from './shared/enrollment-source.service';
import { AuthGuardService } from './shared/services/auth-guard.service';
import { TermService } from './shared/term.service';
import { LoginComponent } from './login/login.component';
import { LogoutComponent } from './logout/logout.component';
import { MainMenuComponent } from './main-menu/main-menu.component';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { NotAuthorizedComponent } from './not-authorized/not-authorized.component';
import { MainMenuAuthGuardService } from './main-menu/main-menu-auth-guard.service';
import { SwitchUserComponent } from './core/switch-user/switch-user.component';
import { AppHttpInterceptor } from './core/app-http/app-http-interceptor';
import { InstructorComponent } from './instructor/instructor.component';
import { RegistrarComponent } from './registrar/registrar.component';
import { MaintenanceComponent } from './registrar/admin/maintenance/maintenance.component';
import { TermListComponent } from './object-list/term-list/term-list.component';
import { CourseListComponent } from './course/course-list/course-list.component';
import { MatTableModule } from '@angular/material/table';
import { RosterLinkComponent } from './roster/roster-link/roster-link.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AddStudentComponent } from './roster/add-student/add-student.component';
import { DownloadGradesComponent } from './roster/download-grades/download-grades.component';
import { EditStudentComponent } from './roster/edit-student/edit-student.component';
import { EmailClassComponent } from './roster/email-class/email-class.component';
import { HelpComponent } from './roster/help/help.component';
import { UploadGradesComponent } from './roster/upload-grades/upload-grades.component';
import { ViewLogComponent } from './roster/view-log/view-log.component';
import { RosterComponent } from './roster/roster.component';
import { MatSelectModule } from '@angular/material/select';
import { GradeStatementComponent } from './roster/grade-statement/grade-statement.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RosterButtonComponent } from './roster/roster-button/roster-button.component';
import { MatRadioModule } from '@angular/material/radio';
import { MatBadgeModule } from '@angular/material/badge';
import { RosterViewCommentComponent } from './roster/roster-view-comment/roster-view-comment.component';
import { RosterMenuAuthGuardService } from './roster/roster-menu-auth-guard.service';
import { ScheduleComponent } from './registrar/admin/maintenance/schedule/schedule.component';
import { RegistrarAdminMenuAuthGuardService } from './registrar/admin/registrar-admin-menu-auth-guard.service';
import { RegistrarStaffMenuAuthGuardService } from './registrar/staff/registrar-staff-menu-auth-guard.service';
import { RegistrarMenuAuthGuardService } from './registrar/registrar-menu-auth-guard.service';
import { MatTabsModule } from '@angular/material/tabs';
import { ScheduleFormComponent } from './registrar/admin/maintenance/schedule/schedule-form/schedule-form.component';
import { ScheduleListComponent } from './registrar/admin/maintenance/schedule/schedule-list/schedule-list.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { ServiceLocator } from './shared/utilities/service-locator';
import { EmailTemplateModule } from './site-admin/email-template/email-template.module';
import { FormConfigurationModule } from './site-admin/form-configuration/form-configuration.module';
import { TablesModule } from './tables/tables.module';
import { FacultyCategoryComponent } from './registrar/admin/maintenance/faculty-category/faculty-category.component';
import { FacultyStaffTypeComponent } from './registrar/admin/maintenance/faculty-staff-type/faculty-staff-type.component';
import { IpGradeRegistrarComponent } from './registrar/staff/ip-grade-registrar/ip-grade-registrar.component';
import { CourseListRegistrarComponent } from './registrar/staff/course-list-registrar/course-list-registrar.component';
import { ValidStaffCodeService } from './shared/valid-staff-code.service';
import { ValidGradingRankCodeService } from './shared/valid-grading-rank-code.service.';
import { GradeChangeCodeMapComponent } from './registrar/admin/maintenance/grade-change-code-map/grade-change-code-map.component';
import { FacultyCategoryEditComponent } from './registrar/admin/maintenance/faculty-category/faculty-category-edit/faculty-category-edit.component';
import { FacultyStaffTypeEditComponent } from './registrar/admin/maintenance/faculty-staff-type/faculty-staff-type-edit/faculty-staff-type-edit.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { GradingRulesComponent } from './registrar/admin/maintenance/grading-rules/grading-rules.component';
import { GradingRuleEditComponent } from './registrar/admin/maintenance/grading-rules/grading-rule-edit/grading-rule-edit.component';
import { IpGradeRegistrarViewStudentComponent } from './registrar/staff/ip-grade-registrar/ip-grade-view-student/ip-grade-registrar-view-student.component';
import { MatDialogModule } from '@angular/material/dialog';
import { GradeStatementContentComponent } from './roster/grade-statement/grade-statement-content/grade-statement-content.component';
import { RosterViewGradeChangeComponent } from './roster/roster-view-grade-change/roster-view-grade-change.component';
import { HealthService } from './shared/services/health.service';
import { GradeChangeCodeMapService } from './shared/grade-change-code-map.service';
import { SubmitConfirmationComponent } from './roster/submit-confirmation/submit-confirmation.component';
import { CourseListRegistrarInstructorComponent } from './registrar/staff/course-list-registrar/course-list-registrar-instructor/course-list-registrar-instructor.component';
import { GradeValidationService } from './shared/grade-validation.service';
import { ScheduleBatchProcessComponent } from './registrar/admin/maintenance/schedule/schedule-batch-process/schedule-batch-process.component';
import { IpGradeSubmitStatusComponent } from './registrar/staff/ip-grade-registrar/ip-grade-submit-status/ip-grade-submit-status-component';
import { NotFoundComponent } from './not-found/not-found.component';

const DEFAULT_DROPZONE_CONFIG: DropzoneConfigInterface = {
    url: '',
    autoReset: 0,
    cancelReset: 0
};

@NgModule({
    declarations: [
        AppComponent,
        LoginComponent,
        LogoutComponent,
        MainMenuComponent,
        NotAuthorizedComponent,
        InstructorComponent,
        RegistrarComponent,
        MaintenanceComponent,
        TermListComponent,
        CourseListComponent,
        RosterComponent,
        RosterLinkComponent,
        AddStudentComponent,
        DownloadGradesComponent,
        EditStudentComponent,
        EmailClassComponent,
        HelpComponent,
        UploadGradesComponent,
        ViewLogComponent,
        GradeStatementComponent,
        RosterButtonComponent,
        RosterViewCommentComponent,
        ScheduleComponent,
        ScheduleFormComponent,
        ScheduleListComponent,
        RosterAddCommentComponent,
        GradeSubmitStatusComponent,
        FacultyCategoryComponent,
        FacultyStaffTypeComponent,
        IpGradeRegistrarComponent,
        IpGradeSubmitStatusComponent,
        CourseListRegistrarComponent,
        GradeChangeCodeMapComponent,
        FacultyCategoryEditComponent,
        FacultyStaffTypeEditComponent,
        GradingRulesComponent,
        GradingRuleEditComponent,
        IpGradeRegistrarViewStudentComponent,
        GradeStatementContentComponent,
        RosterViewGradeChangeComponent,
        SubmitConfirmationComponent,
        CourseListRegistrarInstructorComponent,
        ScheduleBatchProcessComponent,
        NotFoundComponent
    ],
    imports: [
        BrowserModule,
        CommonModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        RouterModule.forRoot(rootRouterConfig, { relativeLinkResolution: 'legacy' }),
        NgIdleKeepaliveModule.forRoot(),
        SharedModule,
        CoreModule,
        DropzoneModule,
        BrowserAnimationsModule,
        NgxPaginationModule,
        MatTableModule,
        MatTooltipModule,
        MatSelectModule,
        MatCheckboxModule,
        MatRadioModule,
        MatBadgeModule,
        MatTabsModule,
        MatExpansionModule,
        MatCardModule,
        MatSortModule,
        MatPaginatorModule,
        UcrFormsModule,
        TablesModule,
        UcrDialogModule,
        UcrTextModule,
        EmailModule,
        MatProgressSpinnerModule,
        MatSidenavModule,
        MatIconModule,
        MatInputModule,
        MatAutocompleteModule,
        MatDialogModule,
        EmailTemplateModule,
        FormConfigurationModule
    ],
    exports: [MatPaginatorModule],
    providers: [
        [
            { provide: LocationStrategy, useClass: PathLocationStrategy },
            { provide: DROPZONE_CONFIG, useValue: DEFAULT_DROPZONE_CONFIG }
        ],
        AuthGuardService,
        Location,
        DatePipe,
        CurrencyPipe,
        MainMenuAuthGuardService,
        RegistrarMenuAuthGuardService,
        RosterMenuAuthGuardService,
        RegistrarAdminMenuAuthGuardService,
        RegistrarStaffMenuAuthGuardService,
        TermService,
        ValidStaffCodeService,
        ValidGradingRankCodeService,
        EnrollmentSourceService,
        GradeChangeCodeMapService,
        GradeValidationService,
        HealthService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppHttpInterceptor,
            multi: true
        }
    ],
    entryComponents: [SwitchUserComponent],
    bootstrap: [AppComponent]
})
export class AppModule {
    constructor(private injector: Injector) {
        ServiceLocator.injector = this.injector;
    }
}
